<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1200"
  >
    <template v-slot:activator="{ on:dialog }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            icon
            v-on="{ ...dialog, ...tooltip }"
          >
            <v-icon color="orange">
              mdi-sync
            </v-icon>
          </v-btn>
        </template>
        <span>Sync latest unpaid leaves</span>
      </v-tooltip>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Sync Latest Unpaid Leaves"
      text="Deduction and Refund"
      class="mb-0"
      :loading="loading"
    > 
      <form v-if="dialog && !loading">
        <v-container>
          <v-row>
            <v-col cols="12">
              Please select the leave applications for unpaid leave deduction and refund 
            </v-col>
            <v-col
              cols="12"
              class="pt-0"
            >
              <v-data-table 
                v-model="selectedLeaveApplications"
                v-validate="'required'"
                dense
                :error-messages="errors.collect('selectedLeaveApplications')"
                data-vv-name="selectedLeaveApplications"
                data-vv-as="leave applications"
                :loading="loading"
                :headers="headers"
                :items="leaveApplications"
                hide-default-footer
                fixed-header
                disable-pagination
                show-select
                item-key="id"
                item-value="id"
                class="elevation-2 mb-3"
                height="330px"
              >
                <template v-slot:top>
                  <h5 class="font-weight-bold py-2 mb-0 pl-2">
                    Leave Application Selection:
                  </h5>
                </template>
                <template v-slot:item.doe="{ item }">
                  <span
                    :class="{
                      'red--text': (!item.company_employee_additional_does[0].payroll_period_id && item.company_employee_additional_does[0].deduction_or_earning.is_negative), 
                      'green--text': ((!item.company_employee_additional_does[0].payroll_period_id && !item.company_employee_additional_does[0].deduction_or_earning.is_negative) || 
                        (item.company_employee_additional_does[0].payroll_period_id && item.company_employee_additional_does[0].payroll_period_id !== payrollPeriod.id)),
                      'grey--text': (item.company_employee_additional_does[0].payroll_period_id && item.company_employee_additional_does[0].payroll_period_id === payrollPeriod.id)
                    }"
                  >
                    {{ !item.company_employee_additional_does[0].payroll_period_id && item.company_employee_additional_does[0].deduction_or_earning.is_negative ? 'DEDUCTION' 
                      : ((!item.company_employee_additional_does[0].payroll_period_id && !item.company_employee_additional_does[0].deduction_or_earning.is_negative) || 
                        (item.company_employee_additional_does[0].payroll_period_id && item.company_employee_additional_does[0].payroll_period_id !== payrollPeriod.id)) ? 'REFUND'
                        : item.company_employee_additional_does[0].payroll_period_id && item.company_employee_additional_does[0].payroll_period_id === payrollPeriod.id ? '*Refund from synced DOE*' 
                          : ''
                    }}
                  </span>   
                </template>
              </v-data-table>
              <span class="red--text text--lighten-1 caption">{{ errors.first('selectedLeaveApplications') }}</span>
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing && !disableSyncButton"
          :disabled="leaveApplications.length < 1 || disableSyncButton"
          color="hrissecondary"    
          class="white--text px-10"           
          @click="submitForm"
        >
          Sync
        </v-btn>
        
        <v-btn
          :loading="progressing && !disableDeleteButton"
          :disabled="leaveApplications.length < 1 || disableDeleteButton"
          color="red"    
          class="white--text px-10"           
          @click="showDeleteDialog"
        >
          Delete
        </v-btn>

        <v-dialog
          v-model="deleteDialog"
          persistent
          max-width="500"
        >
          <material-card
            :offset="10"
            :full-width="true"
            color="warning"
            title="Remove from Sync List"
            text="Remove Unpaid Leave Deduction / Refund from Sync List"
            class="mb-0"
            :loading="loading"
          > 
            <form v-if="!loading">
              <v-container>
                <v-row>
                  <v-col cols="12">
                    Are you sure to remove from sync list?
                  </v-col>
                </v-row>
              </v-container>
            </form>

            <v-card-actions v-if="!loading">
              <v-spacer />
              <v-btn
                :disabled="progressing"
                text
                class="px-10"
                @click="deleteDialog = false; disableSyncButton = false"
              >
                No
              </v-btn>
              <v-btn
                :loading="progressing"
                color="red"    
                class="white--text px-10"           
                @click="submitDeleteForm"
              >
                Yes
              </v-btn>
            </v-card-actions> 
          </material-card>
        </v-dialog>
      </v-card-actions> 
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      payrollPeriod: {
        type: Object,
        required: true,
      },
      payrollCycleTransaction: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        deleteDialog: false,
        disableDeleteButton: false,
        disableSyncButton: false,
        loading: false,
        progressing: false,
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'approval_request.employee.person.name',
          },
          {
            text: 'From',
            align: 'left',
            sortable: true,
            value: 'start_date',
          },
          {
            text: 'To',
            align: 'left',
            sortable: true,
            value: 'end_date',
          },
          {
            text: 'Requested Days',
            align: 'left',
            sortable: true,
            value: 'requested_days',
          },
          {
            text: 'DOE Type',
            align: 'left',
            sortable: false,
            value: 'doe',
          },
        ],
        leaveApplications: [],
        selectedLeaveApplications: [],
      }
    },
    created () {
      this.loadData()
    },
    methods: {
      showDeleteDialog () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
            
            this.deleteDialog = true
            this.disableSyncButton = true
          })
      },
      loadData () {
        this.loading = true

        this.$api.entitlementLeaveApplications().listLeaveApplicationsToSync('?date=' + this.payrollPeriod.payroll_date)
          .then(res => {
            this.leaveApplications = _.orderBy(res.data, ['approval_request.employee.person.name'], ['asc'])  

            this.loading = false
          })
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
            
            this.progressing = true
            this.disableDeleteButton = true

            this.$api.payrollCycleTransactions().sync(this.payrollCycleTransaction.id, this.prepareFormData())
              .then(res => {
                this.$snack({ message: res.data.status })
                this.$emit('synced')

                this.dialog = false
              }).catch(error => {
                this.$snack({ 
                  color: 'error',
                  message: error.response.data.message, 
                })
              }).then(() => {
                this.progressing = false
                this.disableDeleteButton = false
              })
          })
      },
      submitDeleteForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
            
            this.progressing = true
            this.disableSyncButton = true

            this.$api.payrollCycleTransactions().delete(this.payrollCycleTransaction.id, this.prepareFormData())
              .then(res => {
                this.$snack({ message: res.data.status })
                this.$emit('synced')

                this.dialog = false
              }).catch(error => {
                this.$snack({ 
                  color: 'error',
                  message: error.response.data.message, 
                })
              }).then(() => {
                this.progressing = false
                this.disableDeleteButton = false
              })
          })
      },
      getLeaveApplicationIds () {
        let leaveApplicationIds = []

        this.selectedLeaveApplications.map(item => {
          leaveApplicationIds.push(item.id)
        })

        return leaveApplicationIds
      },
      prepareFormData () {
        return {
          payroll_period_id: this.payrollPeriod.id,
          payroll_cycle_id: this.payrollCycleTransaction.payroll_cycle_id,
          leaveApplicationIds: this.getLeaveApplicationIds(),
        }
      },
    },
  }
</script>
